// pages/ProjectsPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import enLocale from '@fullcalendar/core/locales/en-gb';
import axios from 'axios';
import { 
  PlusIcon, 
  XMarkIcon, 
  CalendarIcon, 
  ListBulletIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { 
  Calendar, 
  Clock, 
  Globe, 
  Lock, 
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users,
  Briefcase,
  Target,
  Filter,
  Download
} from 'lucide-react';

import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { API_BASE_URL } from '../config/api';

interface ProjectData {
  _id?: string;
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social';
  isPublic: boolean;
  color: string;
  creator?: { _id: string; name: string; email: string };
}

interface TaskData {
  _id: string;
  title: string;
  description?: string;
  project: { _id: string; title: string; color?: string };
  team?: { _id: string; name: string };
  assignee?: { _id: string; name: string; email: string };
  status: string;
  priority: string;
  deadline?: string;
  startDate?: string;
}

interface DashboardData {
  projects: ProjectData[];
  tasks: TaskData[];
  teams: any[];
  stats: {
    totalProjects: number;
    totalTasks: number;
    upcomingTasks: number;
    overdueTasks: number;
    completedTasks: number;
    inProgressTasks: number;
    teamsCount: number;
  };
  upcomingWeekTasks: TaskData[];
  overdueTasks: TaskData[];
}

interface FormData {
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social';
  color: string;
  isPublic: boolean;
}

const API_BASE = API_BASE_URL;

const ProjectsPage: React.FC = () => {
  const user = useSelector((state: RootState) => selectUser(state));
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    start: '',
    end: '',
    type: 'meeting',
    color: '#3788d8',
    isPublic: true
  });

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?._id) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/projects/dashboard-data`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        setDashboardData(res.data.data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error loading data');
        console.error('Erreur fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [user?._id]);

  // Create project
  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id) return;
    setLoading(true);
    try {
      const payload = { ...formData, creatorId: user._id };
      const res = await axios.post(`${API_BASE}/projects`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Refresh dashboard data
      const dashboardRes = await axios.get(`${API_BASE}/projects/dashboard-data`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDashboardData(dashboardRes.data.data);
      
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        start: '',
        end: '',
        type: 'meeting',
        color: '#3788d8',
        isPublic: true
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating item');
    } finally {
      setLoading(false);
    }
  };

  // Auto-update color based on type
  useEffect(() => {
    const colors: Record<string, string> = {
      meeting: '#3788d8',
      workshop: '#56cc9d',
      deadline: '#ff9f40',
      social: '#9333ea'
    };
    
    const defaultColor = colors[formData.type] || '#3788d8';
    if (formData.color !== defaultColor && Object.values(colors).includes(formData.color)) {
      setFormData(prev => ({ ...prev, color: defaultColor }));
    }
  }, [formData.type]);

  const handleFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type: inputType } = e.target;
    if (inputType === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  const toggleView = useCallback(() => {
    setViewMode(prev => prev === 'calendar' ? 'list' : 'calendar');
  }, []);

  // Navigation vers le projet
  const handleProjectClick = (info: any) => {
    const projectId = info.event?.id || info;
    if (projectId) {
      navigate(`/projects/${projectId}`);
    }
  };

  // Prepare calendar events (projects + tasks)
  const calendarEvents = useMemo(() => {
    if (!dashboardData) return [];
    
    const projectEvents = dashboardData.projects.map((p) => ({
      id: p._id,
      title: p.title,
      start: p.start,
      end: p.end,
      backgroundColor: p.color,
      borderColor: p.color,
      textColor: '#ffffff',
      extendedProps: { type: 'project', project: p }
    }));

    const taskEvents = dashboardData.tasks
      .filter(t => t.deadline || t.startDate)
      .map((t) => ({
        id: `task-${t._id}`,
        title: `📋 ${t.title}`,
        start: t.startDate || t.deadline,
        end: t.deadline || t.startDate,
        backgroundColor: t.project?.color || '#6366f1',
        borderColor: t.project?.color || '#6366f1',
        textColor: '#ffffff',
        extendedProps: { type: 'task', task: t }
      }));

    return [...projectEvents, ...taskEvents];
  }, [dashboardData]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      meeting: 'Meeting',
      workshop: 'Workshop',
      deadline: 'Deadline',
      social: 'Personal'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      meeting: 'bg-blue-100 text-blue-800 border-blue-300',
      workshop: 'bg-green-100 text-green-800 border-green-300',
      deadline: 'bg-orange-100 text-orange-800 border-orange-300',
      social: 'bg-purple-100 text-purple-800 border-purple-300'
    };
    return colors[type] || colors.meeting;
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-gray-100 text-gray-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      done: 'bg-green-100 text-green-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      todo: 'bg-gray-100 text-gray-800',
      blocked: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.todo;
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-500">Please log in to view your projects.</p>
      </div>
    );
  }

  if (loading && !dashboardData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  const projects = dashboardData?.projects || [];
  const tasks = dashboardData?.tasks || [];
  const stats = dashboardData?.stats || {
    totalProjects: 0,
    totalTasks: 0,
    upcomingTasks: 0,
    overdueTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    teamsCount: 0
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
              <p className="text-gray-500 mt-1">Manage your projects and teams</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleView}
                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                {viewMode === 'calendar' ? (
                  <>
                    <ListBulletIcon className="w-5 h-5 mr-2" />
                    List
                  </>
                ) : (
                  <>
                    <CalendarIcon className="w-5 h-5 mr-2" />
                    Calendar
                  </>
                )}
              </button>
              <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </button>
              <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg shadow-md hover:opacity-90 transition"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Nouveau projet
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-12 lg:col-span-3 space-y-6">
            {/* Stats Cards */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Statistics</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Briefcase className="w-4 h-4 text-emerald-600 mr-2" />
                    <span className="text-sm text-gray-600">Projects</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{stats.totalProjects}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Target className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-600">Tasks</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{stats.totalTasks}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm text-gray-600">Completed</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{stats.completedTasks}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TrendingUp className="w-4 h-4 text-orange-600 mr-2" />
                    <span className="text-sm text-gray-600">In progress</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{stats.inProgressTasks}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 text-emerald-600 mr-2" />
                    <span className="text-sm text-gray-600">Teams</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{stats.teamsCount}</span>
                </div>
              </div>
            </div>

            {/* Upcoming Tasks */}
            {dashboardData?.upcomingWeekTasks && dashboardData.upcomingWeekTasks.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Upcoming tasks</h3>
                <div className="space-y-2">
                  {dashboardData.upcomingWeekTasks.slice(0, 5).map((task) => (
                    <div
                      key={task._id}
                      className="p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                      onClick={() => navigate(`/projects/${task.project._id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {task.deadline && formatDate(task.deadline)}
                          </p>
                        </div>
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overdue Tasks */}
            {dashboardData?.overdueTasks && dashboardData.overdueTasks.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4">
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <h3 className="text-sm font-semibold text-red-700">En retard</h3>
                </div>
                <div className="space-y-2">
                  {dashboardData.overdueTasks.slice(0, 5).map((task) => (
                    <div
                      key={task._id}
                      className="p-2 rounded-lg hover:bg-red-50 cursor-pointer transition"
                      onClick={() => navigate(`/projects/${task.project._id}`)}
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                      <p className="text-xs text-red-600 mt-1">
                        {task.deadline && formatDate(task.deadline)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="col-span-12 lg:col-span-9">
            {viewMode === 'calendar' ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 pt-6 pb-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-1">Calendar</h2>
                      <p className="text-sm text-gray-600">
                        {projects.length} {projects.length === 1 ? 'project' : 'projects'} • {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    height="auto"
                    events={calendarEvents}
                    eventClick={handleProjectClick}
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,timeGridWeek,timeGridDay',
                    }}
                    locale={enLocale}
                    firstDay={1}
                    buttonText={{
                      today: 'Aujourd\'hui',
                      month: 'Mois',
                      week: 'Semaine',
                      day: 'Jour',
                    }}
                    eventDisplay="block"
                    dayMaxEvents={3}
                    moreLinkClick="popover"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Project list</h2>
                    <p className="text-sm text-gray-500">
                      {projects.length} {projects.length === 1 ? 'project' : 'projects'} total
                    </p>
                  </div>
                </div>
                {projects.length === 0 ? (
                  <div className="text-center py-16">
                    <Calendar className="h-20 w-20 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No projects</h3>
                    <p className="text-gray-500 mb-6">Create your first project to get started</p>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg shadow-md hover:opacity-90 transition"
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      Create a project
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {projects.map((project) => {
                      const isPast = new Date(project.end) < new Date();
                      const projectTasks = tasks.filter(t => t.project._id === project._id);
                      return (
                        <div
                          key={project._id}
                          className={`group bg-white rounded-xl shadow-md hover:shadow-xl border-2 transition-all duration-300 cursor-pointer overflow-hidden ${
                            isPast ? 'border-gray-200 opacity-75' : 'border-transparent hover:border-emerald-200'
                          }`}
                          onClick={() => handleProjectClick(project._id)}
                        >
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-start space-x-4 flex-1">
                                <div 
                                  className="w-1 h-full min-h-[80px] rounded-full flex-shrink-0"
                                  style={{ backgroundColor: project.color }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3 mb-2">
                                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors">
                                      {project.title}
                                    </h3>
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getTypeColor(project.type)}`}>
                                      {getTypeLabel(project.type)}
                                    </span>
                                    {project.isPublic ? (
                                      <span className="flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs">
                                        <Globe className="h-3 w-3 mr-1" />
                                        Public
                                      </span>
                                    ) : (
                                      <span className="flex items-center px-2 py-1 bg-gray-50 text-gray-700 rounded-md text-xs">
                                        <Lock className="h-3 w-3 mr-1" />
                                        Private
                                      </span>
                                    )}
                                  </div>
                                  {project.description && (
                                    <p className="text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                                  )}
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <div className="flex items-center text-gray-700">
                                      <Calendar className="h-4 w-4 mr-2 text-emerald-500 flex-shrink-0" />
                                      <div>
                                        <p className="text-xs text-gray-500">Start</p>
                                        <p className="text-sm font-medium">{formatDate(project.start)}</p>
                                        <p className="text-xs text-gray-500">{formatTime(project.start)}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center text-gray-700">
                                      <Calendar className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0" />
                                      <div>
                                        <p className="text-xs text-gray-500">End</p>
                                        <p className="text-sm font-medium">{formatDate(project.end)}</p>
                                        <p className="text-xs text-gray-500">{formatTime(project.end)}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center text-gray-700">
                                      <Target className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                                      <div>
                                        <p className="text-xs text-gray-500">Tasks</p>
                                        <p className="text-sm font-medium">{projectTasks.length}</p>
                                      </div>
                                    </div>
                                  </div>
                                  {project.creator && (
                                    <div className="flex items-center text-gray-600 pt-3 border-t border-gray-100">
                                      <div className="h-6 w-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center mr-2">
                                        <span className="text-xs font-bold text-white">
                                          {project.creator.name?.charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                      <span className="text-sm">{project.creator.name}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex-shrink-0 ml-4">
                                <div className="h-10 w-10 rounded-full bg-gray-100 group-hover:bg-gradient-to-r group-hover:from-emerald-500 group-hover:to-teal-500 flex items-center justify-center transition-all duration-300">
                                  <ArrowRight className="h-5 w-5 text-gray-600 group-hover:text-white transition-colors" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center border-b border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-800">New project</h3>
              <button 
                onClick={() => setShowCreateModal(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleCreateProject} className="p-6 space-y-4">
          <input
            name="title"
                placeholder="Title"
            required
            value={formData.title}
            onChange={handleFormChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <textarea
            name="description"
            placeholder="Description"
            value={formData.description}
            onChange={handleFormChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent h-20"
          />
          <input
            type="datetime-local"
            name="start"
            required
            value={formData.start}
            onChange={handleFormChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <input
            type="datetime-local"
            name="end"
            required
            value={formData.end}
            onChange={handleFormChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <select
            name="type"
            value={formData.type}
            onChange={handleFormChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
                <option value="meeting">Meeting</option>
                <option value="workshop">Workshop</option>
                <option value="deadline">Deadline</option>
                <option value="social">Personal</option>
          </select>
          <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleFormChange}
                className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleFormChange}
                placeholder="#3788d8"
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
              />
            </div>
          </div>
          <label className="flex items-center space-x-2">
            <input
              name="isPublic"
              type="checkbox"
              checked={formData.isPublic}
              onChange={handleFormChange}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-700">Public</span>
          </label>
          <button
            type="submit"
            disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
          >
                {loading ? 'Creating...' : 'Create'}
          </button>
        </form>
      </div>
                        </div>
                      )}
    </div>
  );
};

export default ProjectsPage;
